// The two images printed in the invoice signature block.
//
//   Authorised Signature
//   [ signature ][ SEAL ]      <- signature sits flush against the seal's left
//   ─────────────────────
//        Company Name
//
// The signature is drawn in the app (nobody has a scan of it handy); the seal
// is uploaded, because it's a generated artwork you already own. Both live in
// Firebase Storage with their URL on the admin profile.

import { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Button, IconButton, AlertBanner, Tabs, Spinner, cx } from "./UI";
import { useAuth } from "../context/AuthContext";
import { uploadStampFile, deleteStampFile } from "../utils/firebase";
import api from "../utils/api";
import toast from "react-hot-toast";
import { PenLine, Eraser, Undo2, Trash2, Stamp, Upload, ImageOff } from "lucide-react";

// Canvas CSS size. 3:1 suits a signature and matches the invoice block.
const W = 600;
const H = 200;
const STROKE = "#111827";

const MAX_SEAL_BYTES = 2 * 1024 * 1024;
const SEAL_TYPES = ["image/png", "image/jpeg", "image/webp"];

/* ── The drawing surface ───────────────────────────────────────────────────── */
function Canvas({ canvasRef, strokes, setStrokes }) {
  const drawing = useRef(false);
  const current = useRef(null);

  // Repaint from the stroke list — this is what makes undo/clear trivial
  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = canvas.width / W;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.strokeStyle = STROKE;
    ctx.fillStyle = STROKE;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    for (const stroke of strokes) {
      if (stroke.length < 2) {
        // a tap — draw a dot so a full stop or an i-dot survives
        const [p] = stroke;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.25, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
      ctx.stroke();
    }
  }, [canvasRef, strokes]);

  // Size the backing store to the device pixel ratio so print stays crisp
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    repaint();
  }, [canvasRef, repaint]);

  useEffect(() => { repaint(); }, [repaint]);

  const pointAt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * W,
      y: ((e.clientY - rect.top) / rect.height) * H,
    };
  };

  const start = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    current.current = [pointAt(e)];
    setStrokes(prev => [...prev, current.current]);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    current.current.push(pointAt(e));
    // mutating the last stroke in place, so nudge React with a new array identity
    setStrokes(prev => [...prev.slice(0, -1), current.current]);
  };

  const end = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    current.current = null;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };

  return (
    <div className="relative rounded-2xl border-2 border-dashed border-ink-300 bg-white overflow-hidden">
      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        style={{ aspectRatio: `${W} / ${H}` }}
        className="w-full block cursor-crosshair touch-none"
      />
      {/* signing line + hint, purely visual — never exported */}
      <div className="absolute inset-x-8 bottom-8 border-b border-ink-200 pointer-events-none" />
      {strokes.length === 0 && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <span className="text-sm text-ink-300 flex items-center gap-2">
            <PenLine size={15} /> Sign here
          </span>
        </div>
      )}
    </div>
  );
}

/* ── How the pair will print ───────────────────────────────────────────────── */
function BlockPreview({ signatureUrl, sealUrl, companyName }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-2">
        How it prints on the invoice
      </p>
      <div className="rounded-2xl border border-ink-200 bg-white p-4">
        <div className="text-right">
          <div className="text-[11px] text-ink-400 mb-1.5">Authorised Signature</div>
          {/* flush against each other, exactly as the PDF lays them out */}
          <div className="flex items-center justify-end">
            {signatureUrl
              ? <img src={signatureUrl} alt="Signature" className="h-12 max-w-[130px] object-contain" />
              : <span className="text-[11px] text-ink-300 pr-2">no signature</span>}
            <img
              src={sealUrl || "/seals/HILANDSEAL.png"} alt="Seal"
              className="h-[68px] object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="border-t border-ink-700 pt-1.5 mt-1 text-xs text-ink-500">
            {companyName || "Company Name"}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Signature tab ─────────────────────────────────────────────────────────── */
function SignatureTab({ user, persist, busy, setBusy, onDone }) {
  const canvasRef = useRef(null);
  const [strokes, setStrokes] = useState([]);
  const existing = user?.signatureUrl;
  const adminId = user?.adminId || user?._id || user?.id;

  const save = async () => {
    if (!strokes.length) return toast.error("Draw your signature first");
    if (!adminId) return toast.error("Could not identify your account — sign in again");

    setBusy(true);
    try {
      const blob = await new Promise((resolve, reject) =>
        canvasRef.current.toBlob(b => (b ? resolve(b) : reject(new Error("Could not read the canvas"))), "image/png"));

      const { firebasePath, firebaseURL } = await uploadStampFile(blob, adminId, "signature");
      const previousPath = user?.signaturePath;

      await persist({ signatureUrl: firebaseURL, signaturePath: firebasePath });
      if (previousPath && previousPath !== firebasePath) await deleteStampFile(previousPath);

      toast.success("Signature saved");
      setStrokes([]);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save signature");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your signature? Invoices will print with the seal only.")) return;
    setBusy(true);
    try {
      const previousPath = user?.signaturePath;
      await persist({ signatureUrl: null, signaturePath: null });
      if (previousPath) await deleteStampFile(previousPath);
      toast.success("Signature removed");
      setStrokes([]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove signature");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {existing && (
        <div>
          <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-2">Current signature</p>
          <div className="flex items-center gap-3 bg-ink-50 border border-ink-200 rounded-2xl p-3">
            <img src={existing} alt="Current signature" className="h-14 w-auto object-contain" />
            <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={remove}
              disabled={busy} title="Remove signature" className="ml-auto" />
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider">
            {existing ? "Draw a new one" : "Draw your signature"}
          </p>
          <div className="flex items-center gap-1">
            <IconButton size="sm" variant="outline" icon={<Undo2 size={15} />} onClick={() => setStrokes(p => p.slice(0, -1))}
              disabled={!strokes.length || busy} title="Undo last stroke" />
            <IconButton size="sm" variant="outline" icon={<Eraser size={15} />} onClick={() => setStrokes([])}
              disabled={!strokes.length || busy} title="Clear" />
          </div>
        </div>

        <Canvas canvasRef={canvasRef} strokes={strokes} setStrokes={setStrokes} />

        <p className="text-xs text-ink-400 mt-2">
          Use a mouse, trackpad, pen or your finger. Saved with a transparent background
          so it sits on the invoice rather than in a white box.
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} loading={busy} disabled={!strokes.length || busy} block className="sm:w-auto">
          Save Signature
        </Button>
      </div>
    </div>
  );
}

/* ── Seal tab ──────────────────────────────────────────────────────────────── */
function SealTab({ user, persist, busy, setBusy, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const existing = user?.sealUrl;
  const adminId = user?.adminId || user?._id || user?.id;

  // Revoke the object URL when it's replaced or the tab unmounts
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const pick = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";                       // re-picking the same file still fires
    if (!picked) return;

    if (!SEAL_TYPES.includes(picked.type)) return toast.error("Use a PNG, JPG or WebP image");
    if (picked.size > MAX_SEAL_BYTES) return toast.error("Seal must be under 2 MB");

    if (preview) URL.revokeObjectURL(preview);
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
  };

  const save = async () => {
    if (!file) return toast.error("Choose a seal image first");
    if (!adminId) return toast.error("Could not identify your account — sign in again");

    setBusy(true);
    try {
      const { firebasePath, firebaseURL } = await uploadStampFile(file, adminId, "seal");
      const previousPath = user?.sealPath;

      await persist({ sealUrl: firebaseURL, sealPath: firebasePath });
      if (previousPath && previousPath !== firebasePath) await deleteStampFile(previousPath);

      toast.success("Seal saved");
      setFile(null);
      setPreview(null);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save seal");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your seal? Invoices fall back to the built-in HILAND seal.")) return;
    setBusy(true);
    try {
      const previousPath = user?.sealPath;
      await persist({ sealUrl: null, sealPath: null });
      if (previousPath) await deleteStampFile(previousPath);
      toast.success("Seal removed");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove seal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-2">
          {existing ? "Current seal" : "Seal in use"}
        </p>
        <div className="flex items-center gap-3 bg-ink-50 border border-ink-200 rounded-2xl p-3">
          <img
            src={existing || "/seals/HILANDSEAL.png"} alt="Seal"
            className="h-20 w-auto object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <span className="text-xs text-ink-400">
            {existing ? "Your uploaded seal" : "Built-in HILAND seal — upload one to replace it"}
          </span>
          {existing && (
            <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={remove}
              disabled={busy} title="Remove seal" className="ml-auto" />
          )}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-2">
          {existing ? "Upload a new one" : "Upload your seal"}
        </p>

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className={cx(
            "w-full rounded-2xl border-2 border-dashed p-6 grid place-items-center gap-2 transition-colors",
            "disabled:opacity-50 disabled:pointer-events-none",
            preview ? "border-brand-300 bg-brand-50/40" : "border-ink-300 bg-white hover:border-brand-400 hover:bg-brand-50/30"
          )}
        >
          {preview ? (
            <>
              <img src={preview} alt="Seal preview" className="h-24 w-auto object-contain" />
              <span className="text-xs text-brand-700 font-medium">{file?.name} — click to choose another</span>
            </>
          ) : (
            <>
              <span className="w-11 h-11 rounded-xl bg-ink-100 text-ink-400 grid place-items-center">
                <Upload size={19} />
              </span>
              <span className="text-sm text-ink-600 font-medium">Choose a seal image</span>
              <span className="text-xs text-ink-400">PNG, JPG or WebP · up to 2 MB</span>
            </>
          )}
        </button>

        <input ref={fileRef} type="file" accept={SEAL_TYPES.join(",")} onChange={pick} className="hidden" />

        <p className="text-xs text-ink-400 mt-2 flex items-start gap-1.5">
          <ImageOff size={13} className="shrink-0 mt-0.5" />
          A PNG with a transparent background prints best — a JPG carries a white
          box that will cover the invoice behind it.
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} loading={busy} disabled={!file || busy} block className="sm:w-auto">
          Save Seal
        </Button>
      </div>
    </div>
  );
}

/* ── The modal ─────────────────────────────────────────────────────────────── */
export default function SignaturePad({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("signature");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setTab("signature"); }, [open]);

  const persist = useCallback(async (body) => {
    const { data } = await api.put("/auth/admin/stamps", body);
    await refreshUser();
    return data;
  }, [refreshUser]);

  const shared = { user, persist, busy, setBusy, onDone: onClose };

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Signature & Seal"
      subtitle="Printed together in the signature block of every invoice"
      icon={<PenLine size={18} />}
      size="lg"
    >
      <div className="space-y-4">
        <Tabs
          active={tab}
          onChange={setTab}
          variant="pill"
          tabs={[
            { key: "signature", label: "Signature", icon: <PenLine size={14} /> },
            { key: "seal", label: "Seal", icon: <Stamp size={14} /> },
          ]}
        />

        <BlockPreview signatureUrl={user?.signatureUrl} sealUrl={user?.sealUrl} companyName={user?.company} />

        {tab === "signature" ? <SignatureTab {...shared} /> : <SealTab {...shared} />}

        <AlertBanner
          type="info"
          message="These print on every invoice, including ones issued earlier. Changing them changes what those invoices look like when reprinted."
        />

        {busy && (
          <div className="flex items-center gap-2 text-xs text-ink-400 justify-end">
            <Spinner size="xs" /> Working…
          </div>
        )}
      </div>
    </Modal>
  );
}
